'use client'
import React, { FC, useState } from 'react'
import { StarRatingIcon } from '@/components/icons/star-rating-icon'
import Image from 'next/image'
import studentImg1 from '@/images/home/ellipse3.png'
import studentImg2 from '@/images/home/ellipse4.png'
import studentImg3 from '@/images/home/ellipse5.png'
import { IHomePageTemplateHeroBlock } from '@/types/firebase'
import {
  BaseModal,
  CommonSignUpModalContent,
} from '@/components/ui/common/modal'

interface Props {
  customContent: IHomePageTemplateHeroBlock
}

export const CommonHeroBlock: FC<Props> = ({
  customContent: { heroTitle, subtitle, footnote, image },
}) => {
  const [showModal, setShowModal] = useState(false)
  const [hoveredElementId, setHoveredElementId] = useState<string>()

  return (
    <>
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className="absolute left-[max(45rem,calc(50%+8rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
          aria-hidden="true"
        >
          <div
            className=" w-[36.0625rem] bg-gradient-to-r from-[#ff80b5] to-[#9089fc] opacity-30"
            style={{
              clipPath:
                'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
            }}
          />
        </div>
        <div className="lg:grid lg:grid-cols-2">
          {/* Left Side */}
          <div className={'pt-16'}>
            {/* Title */}
            <div className="">
              {heroTitle && (
                <h1
                  id="title"
                  className="lg:text-left text-center transition-colors"
                  style={{
                    fontSize: `${heroTitle.title.styles.fontSize}px`,
                    fontWeight: +heroTitle.title.styles.fontWeight,
                    lineHeight: `${heroTitle.title.styles.lineHeight}px`,
                    fontStyle: heroTitle.title.styles.isItalic
                      ? 'italic'
                      : 'normal',
                    color:
                      hoveredElementId === 'title'
                        ? heroTitle.title.styles.onHoverColor
                        : heroTitle.title.styles.color,
                  }}
                  onMouseEnter={() => setHoveredElementId('title')}
                  onMouseLeave={() => setHoveredElementId(undefined)}
                >
                  {heroTitle.title.text}
                  {heroTitle.accentText && (
                    <span
                      id="accentText transition-colors"
                      style={{
                        fontSize: `${heroTitle.accentText.styles.fontSize}px`,
                        fontWeight: +heroTitle.accentText.styles.fontWeight,
                        lineHeight: `${heroTitle.accentText.styles.lineHeight}px`,
                        fontStyle: heroTitle.accentText.styles.isItalic
                          ? 'italic'
                          : 'normal',
                        color:
                          hoveredElementId === 'accentText'
                            ? heroTitle.accentText.styles.onHoverColor
                            : heroTitle.accentText.styles.color,
                      }}
                      onMouseEnter={() => setHoveredElementId('accentText')}
                      onMouseLeave={() => setHoveredElementId(undefined)}
                    >
                      {' ' + heroTitle.accentText.text}
                    </span>
                  )}
                  {heroTitle.asterisk && (
                    <span
                      id="asterisk"
                      className="opacity-50 transition-colors"
                      style={{
                        fontSize: `${heroTitle.asterisk.styles.fontSize}px`,
                        fontWeight: +heroTitle.asterisk.styles.fontWeight,
                        lineHeight: `${heroTitle.asterisk.styles.lineHeight}px`,
                        fontStyle: heroTitle.asterisk.styles.isItalic
                          ? 'italic !important'
                          : 'normal',
                        color:
                          hoveredElementId === 'asterisk'
                            ? heroTitle.asterisk.styles.onHoverColor
                            : heroTitle.asterisk.styles.color,
                      }}
                      onMouseEnter={() => setHoveredElementId('asterisk')}
                      onMouseLeave={() => setHoveredElementId(undefined)}
                    >
                      {heroTitle.asterisk.text}
                    </span>
                  )}
                </h1>
              )}
            </div>

            {/* Subtitle */}
            <div className="lg:h-[72px] lg:w-[440px]">
              {subtitle && (
                <h2
                  id="subtitle"
                  className="mt-[20px] lg:text-left text-center transition-colors"
                  style={{
                    fontSize: `${subtitle.styles.fontSize}px`,
                    fontWeight: +subtitle.styles.fontWeight,
                    lineHeight: `${subtitle.styles.lineHeight}px`,
                    fontStyle: subtitle.styles.isItalic ? 'italic' : 'normal',
                    color:
                      hoveredElementId === 'subtitle'
                        ? subtitle.styles.onHoverColor
                        : subtitle.styles.color,
                  }}
                  onMouseEnter={() => setHoveredElementId('subtitle')}
                  onMouseLeave={() => setHoveredElementId(undefined)}
                >
                  {subtitle.text}
                </h2>
              )}
            </div>

            <button
              onClick={() => setShowModal(true)}
              className="duration-300  v8-violet-button text-[16px] block cursor-pointer font-semibold py-[10px] lg:mx-0 mx-auto text-center mt-[20px] border-1 border-white w-[173px] rounded-[8px] shadow-md"
            >
              {'Learn with Us'}
            </button>

            {/* Ratings Desktop */}
            <div className="max-lg:hidden w-full flex flex-col">
              <div className="mt-6 flex items-center gap-4">
                {/* Student Avatars */}
                <div className="flex items-center">
                  <div className="w-[60.52px] h-[60.52px] rounded-[50%] z-20 border-white">
                    <Image src={studentImg1} alt={'Ellipse3'} />
                  </div>
                  <div className="w-[60.52px] h-[60.52px] ml-[-20px] z-10 rounded-[50%] border-white">
                    <Image src={studentImg2} alt={'Ellipse4'} />
                  </div>
                  <div className="w-[60.52px] h-[60.52px] z-0 ml-[-20px] rounded-[50%] border-white">
                    <Image src={studentImg3} alt={'Ellipse5'} />
                  </div>
                </div>

                <div className="h-full flex flex-row items-center justify-center gap-4">
                  {/* Happy Students */}
                  <div className="flex justify-end">
                    <div className="flex flex-col items-center">
                      <p className="text-3xl text-center font-bold text-v8-violet">
                        24,000
                      </p>
                      <p className="text-base text-center text-v8-ink text-nowrap">
                        Happy students
                      </p>
                    </div>
                  </div>

                  {/* Divider */}
                  <div
                    id="divider"
                    className="h-full min-h-14 w-[2px] bg-v8-line self-center"
                  ></div>

                  {/* Tutor Ratings */}
                  <div className="flex justify-start">
                    <div className="flex flex-col items-center">
                      {/* Value */}
                      <p className="text-3xl text-center font-bold text-v8-violet">
                        4.8/5
                      </p>

                      {/* Text */}
                      <p className="text-base text-center text-v8-ink text-nowrap">
                        Tutor ratings
                      </p>
                    </div>

                    {/* Starts */}
                    <div className="flex items-end">
                      <span className="flex pl-2 text-cyellow">
                        <StarRatingIcon />
                        <StarRatingIcon />
                        <StarRatingIcon />
                        <StarRatingIcon />
                        <StarRatingIcon className="w-6 h-6 opacity-40" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footnote */}
              {footnote?.text && (
                <p
                  id="footnote"
                  className="mt-3 lg:text-left text-center transition-colors"
                  style={{
                    fontSize: `${footnote.styles.fontSize}px`,
                    fontWeight: +footnote.styles.fontWeight,
                    lineHeight: `${footnote.styles.lineHeight}px`,
                    fontStyle: footnote.styles.isItalic ? 'italic' : 'normal',
                    color:
                      hoveredElementId === 'footnote'
                        ? footnote.styles.onHoverColor
                        : footnote.styles.color,
                  }}
                  onMouseEnter={() => setHoveredElementId('footnote')}
                  onMouseLeave={() => setHoveredElementId(undefined)}
                >
                  {footnote.text}
                </p>
              )}
            </div>

            {/* Ratings Mobile */}
            <div className="lg:hidden mt-6 flex flex-col items-center gap-2">
              {/* Student Avatars */}
              <div className="flex items-center">
                <div className="w-[60.52px] h-[60.52px] rounded-[50%] z-20 border-white">
                  <Image src={studentImg1} alt={'Ellipse3'} />
                </div>
                <div className="w-[60.52px] h-[60.52px] ml-[-20px] z-10 rounded-[50%] border-white">
                  <Image src={studentImg2} alt={'Ellipse4'} />
                </div>
                <div className="w-[60.52px] h-[60.52px] z-0 ml-[-20px] rounded-[50%] border-white">
                  <Image src={studentImg3} alt={'Ellipse5'} />
                </div>
              </div>

              <div className="h-full w-full flex flex-row items-center justify-center gap-6">
                {/* Happy Students */}
                <div className="flex w-1/2 justify-end">
                  <div className="flex flex-col items-center">
                    <p className="text-3xl text-center font-bold text-v8-violet">
                      24,000
                    </p>
                    <p className="mt-3 text-base text-center text-v8-ink text-nowrap">
                      Happy students
                    </p>
                  </div>
                </div>

                {/* Divider */}
                <div
                  id="divider"
                  className="h-full min-h-16 w-[1px] bg-v8-line self-center"
                ></div>

                {/* Tutor Ratings */}
                <div className="flex w-1/2 justify-start">
                  <div className="flex flex-col items-center">
                    {/* Value */}
                    <p className="text-3xl text-center font-bold text-v8-violet">
                      4.8/5
                    </p>

                    {/* Starts */}
                    <div className="ml-1 flex text-cyellow">
                      <StarRatingIcon className="w-3 h-3" />
                      <StarRatingIcon className="w-3 h-3" />
                      <StarRatingIcon className="w-3 h-3" />
                      <StarRatingIcon className="w-3 h-3" />
                      <StarRatingIcon className="w-3 h-3 opacity-40" />
                    </div>
                    {/* Text */}
                    <p className="text-base text-center text-v8-ink text-nowrap">
                      Tutor ratings
                    </p>
                  </div>
                </div>
              </div>

              {/* Footnote */}
              {footnote?.text && (
                <p
                  id="footnote"
                  className="mt-3 lg:text-left text-center transition-colors"
                  style={{
                    fontSize: `${footnote.styles.fontSize}px`,
                    fontWeight: +footnote.styles.fontWeight,
                    lineHeight: `${footnote.styles.lineHeight}px`,
                    fontStyle: footnote.styles.isItalic ? 'italic' : 'normal',
                    color:
                      hoveredElementId === 'footnote'
                        ? footnote.styles.onHoverColor
                        : footnote.styles.color,
                  }}
                  onMouseEnter={() => setHoveredElementId('footnote')}
                  onMouseLeave={() => setHoveredElementId(undefined)}
                >
                  {footnote.text}
                </p>
              )}
            </div>
          </div>

          {/* Right Side */}
          {image && (
            <div className="relative lg:mt-0 mt-[60px]">
              <Image
                src={image.src}
                alt="Hero"
                priority
                fill
                className="max-w-[600px] max-h-[600px]"
              />
            </div>
          )}
        </div>
      </div>

      <BaseModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        content={<CommonSignUpModalContent />}
        customZIndexClass="z-[100]"
        bodyAdditionalClassName="max-sm:w-full"
      />
    </>
  )
}
